/** Render-resolution presets for the Settings "Resolution" picker, plus the drawing-buffer
 *  pixel-ratio math the Renderer uses to honour the chosen mode.
 *
 *  A browser can't change the physical panel resolution — the Fullscreen API only makes an
 *  element fill the screen. What we *can* control is the WebGL drawing-buffer resolution (the
 *  internal render target). "Native" renders at the panel's full pixel density; lower modes
 *  downscale the render for performance while the canvas still fills the (fullscreen) viewport.
 *  So "resolution" here means *render* resolution. */

export interface ResMode {
  /** Target drawing-buffer height in physical px. 0 == native (full devicePixelRatio). */
  height: number
  label: string
}

/** Standard 16:9 vertical resolutions, descending. */
const STANDARD_HEIGHTS = [2160, 1440, 1080, 900, 768, 720, 480]

/** Never let an explicit mode collapse the render below this fraction of the viewport. */
const MIN_RATIO = 0.5

/** Build the picker's mode list: "Native (W×H)" first, then every standard mode strictly
 *  below the native height (matching or exceeding native is pointless — it can only waste GPU).
 *  Explicit modes are labelled by render *height* only (`1080p`) — we honour the target height,
 *  not a width, and the rendered width tracks the live viewport aspect, so a fixed `W×H` label would
 *  over-promise exact pixels we don't always produce (windowed play, non-16:9 panels, device caps). */
export function buildResolutionModes(nativeW: number, nativeH: number): ResMode[] {
  const modes: ResMode[] = [{ height: 0, label: `Native (${nativeW}×${nativeH})` }]
  for (const h of STANDARD_HEIGHTS) {
    if (h < nativeH) modes.push({ height: h, label: `${h}p` })
  }
  return modes
}

/** Drawing-buffer pixel ratio for a chosen render height.
 *  - `targetH <= 0` → native: `min(deviceDpr, qualityCap)` (unchanged legacy behaviour).
 *  - `targetH > 0`  → explicit: `targetH / cssH`, never supersampling past the device's physical
 *    pixels and never collapsing below {@link MIN_RATIO}. The quality cap is *bypassed* because
 *    the user picked this resolution on purpose. */
export function computePixelRatio(targetH: number, cssH: number, deviceDpr: number, qualityCap: number): number {
  if (cssH <= 0) return 1
  if (targetH <= 0) return Math.min(deviceDpr, qualityCap)
  // Apply the usability floor, but never let it push above the device's physical pixels — under heavy
  // browser zoom-out `deviceDpr` can fall below MIN_RATIO, and a bare `max(MIN_RATIO, …)` would
  // supersample past the panel, breaking the "never beyond device pixels" invariant.
  return Math.min(deviceDpr, Math.max(MIN_RATIO, Math.min(targetH / cssH, deviceDpr)))
}

/** Native panel resolution in physical px (screen CSS size × devicePixelRatio). */
export function nativePanelSize(): { w: number; h: number } {
  const dpr = window.devicePixelRatio || 1
  return {
    w: Math.round(window.screen.width * dpr),
    h: Math.round(window.screen.height * dpr),
  }
}
